import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiImage, 
  FiInfo, 
  FiCalendar, 
  FiShield, 
  FiThermometer,
  FiClock,
  FiMapPin
} = FiIcons;

const ProductProfile = ({ productId }) => {
  const [selectedImage, setSelectedImage] = useState(0);

  // Mock product data
  const productData = {
    name: 'Abiraterone Acetate Tablets',
    dosageForm: 'Tablets',
    strengths: ['250mg', '500mg'],
    indication: 'Treatment of metastatic castration-resistant prostate cancer',
    innovatorName: 'Janssen Pharmaceuticals',
    brandName: 'Zytiga',
    approvalDate: '2023-08-15',
    launchDate: '2023-09-01',
    rems: 'No',
    deaSchedule: 'None',
    storageCondition: 'Room Temperature',
    ndsriStatus: 'NDSRI Free',
    shelfLife: '36 Months',
    manufacturedIn: 'India',
    images: [
      'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1563213126-a4273aed2016?w=400&h=300&fit=crop'
    ]
  };

  const profileSections = [
    {
      title: 'Basic Information',
      items: [
        { label: 'Product Name', value: productData.name, icon: FiInfo },
        { label: 'Dosage Form', value: productData.dosageForm, icon: FiInfo },
        { label: 'Available Strengths', value: productData.strengths.join(', '), icon: FiInfo },
        { label: 'Indication', value: productData.indication, icon: FiInfo }
      ]
    },
    {
      title: 'Regulatory Information',
      items: [
        { label: 'Innovator Name', value: productData.innovatorName, icon: FiShield },
        { label: 'Brand Name', value: productData.brandName, icon: FiShield },
        { label: 'Approval Date', value: new Date(productData.approvalDate).toLocaleDateString(), icon: FiCalendar },
        { label: 'Launch Date', value: new Date(productData.launchDate).toLocaleDateString(), icon: FiCalendar },
        { label: 'REMS', value: productData.rems, icon: FiShield },
        { label: 'DEA Schedule', value: productData.deaSchedule, icon: FiShield }
      ]
    },
    {
      title: 'Manufacturing & Storage',
      items: [
        { label: 'Storage Condition', value: productData.storageCondition, icon: FiThermometer },
        { label: 'NDSRI Status', value: productData.ndsriStatus, icon: FiShield },
        { label: 'Shelf Life', value: productData.shelfLife, icon: FiClock },
        { label: 'Manufactured In', value: productData.manufacturedIn, icon: FiMapPin }
      ]
    }
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Product Images Gallery */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <SafeIcon icon={FiImage} className="text-blue-600" />
          Product Images
        </h3>
        
        {/* Main Image */}
        <div className="mb-4">
          <img
            src={productData.images[selectedImage]}
            alt={`${productData.name} - View ${selectedImage + 1}`}
            className="w-full h-64 object-cover rounded-lg"
          />
        </div>
        
        {/* Thumbnail Gallery */}
        <div className="grid grid-cols-3 gap-2">
          {productData.images.map((image, index) => (
            <button
              key={index}
              onClick={() => setSelectedImage(index)}
              className={`relative overflow-hidden rounded-lg border-2 transition-all ${
                selectedImage === index 
                  ? 'border-blue-500 ring-2 ring-blue-200' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-20 object-cover"
              />
            </button>
          ))}
        </div>
        
        {/* SKU Information */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Available SKUs</h4>
          <div className="space-y-2">
            {productData.strengths.map((strength, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-sm text-gray-700">{strength}</span>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  NDC: 72578-00{index + 1}-30
                </span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Product Information */}
      <div className="lg:col-span-2 space-y-6">
        {profileSections.map((section, sectionIndex) => (
          <motion.div
            key={section.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: sectionIndex * 0.1 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {section.title}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {section.items.map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <SafeIcon icon={item.icon} className="text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.label}</p>
                    <p className="text-sm text-gray-700 mt-1">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ProductProfile;